import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { AttendanceApi, StaffAttendanceSummaryRow } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useDefaultYear } from './useDefaultYear';

const columnHelper = createColumnHelper<StaffAttendanceSummaryRow>();

const COLUMNS = [
  columnHelper.accessor('personName', { header: 'Name' }),
  columnHelper.accessor('present', { header: 'Present', cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('absent', { header: 'Absent', cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('other', { header: 'Other', cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge> }),
];

export function TeacherAttendanceSummaryPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'personName', defaultSortOrder: 'asc' });
  const { selectedYear, isLoading: yearLoading, hasAnyData } = useDefaultYear();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (selectedYear) {
      setStartDate(selectedYear.startDate.slice(0, 10));
      setEndDate(selectedYear.endDate.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'teachers-summary', dt.params, startDate, endDate],
    queryFn: () =>
      AttendanceApi.teacherSummary({ ...dt.params, startDate: startDate || undefined, endDate: endDate || undefined }),
    enabled: !yearLoading && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Teacher Attendance Summary" description="Present/absent totals per teacher over a date range." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name"
            className="pl-9"
          />
        </div>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
      ) : (
        <DataTable
          columns={COLUMNS}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.personId}
        />
      )}
    </>
  );
}
